import { memo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import {
  Input,
  Label,
  FormGroup,
  FormFeedback,
  type InputProps,
} from "reactstrap";

type Option = { value: string; label: string; disabled?: boolean };

type Props = {
  name: string;
  label?: string;
  options: Option[];
  placeholderOption?: string;
  className?: string;
  inputClassName?:string
  optionClassName?:string
  required?: boolean;
} & Omit<InputProps, "name" | "type" | "invalid" | "id">;

const RHFSelectField = memo(function RHFSelectField({
  name,
  label,
  options,
  placeholderOption,
  className,
  inputClassName,
  optionClassName,
  required = false,
  ...rest
}: Props) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <FormGroup className={className}>
          {label && <Label for={name}>{label}</Label>}
          {label && required && (
            <span
              className="ms-1 px-1 py-0 rounded-1 f-10 fw-bold"
              style={{
                background: "#FEE2E2",
                color: "#B91C1C",
                verticalAlign: "middle",
              }}
            >
              required
            </span>
          )}
          <Input
            id={name}
            type="select"
            className={inputClassName}
            invalid={!!fieldState.error}
            value={field.value ?? ""}
            onChange={(e) => {
              const next = e.target.value === "" ? "" : e.target.value;
              const chosen = options.find((o) => o.value === next);
              if (chosen?.disabled) return; // block disabled picks
              field.onChange(next === "" ? null : next);
            }}
            {...rest}
          >
            {placeholderOption && <option value="">{placeholderOption}</option>}
            {options.map((opt) => (
              <option key={opt.value} value={opt.value} disabled={opt.disabled} className={optionClassName}>
                {opt.label}
              </option>
            ))}
          </Input>
          {fieldState.error && (
            <FormFeedback>{fieldState.error.message}</FormFeedback>
          )}
        </FormGroup>
      )}
    />
  );
});

export default RHFSelectField;
